import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet-async';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { vehicleService } from '../services/vehicleService';
import { formatVehicleTitle } from '../utils/textFormatting';
import PaymentModal from '../components/PaymentModal';

const { FiFilter, FiGrid, FiList, FiHeart, FiPhone } = FiIcons;

const Inventory = () => {
  const [vehicles, setVehicles] = useState([]);
  const [filteredVehicles, setFilteredVehicles] = useState([]);
  const [filters, setFilters] = useState({
    make: '',
    model: '',
    year: '',
    priceMin: '',
    priceMax: '',
    mileageMax: '',
    fuelType: '',
    transmission: '',
    condition: '',
    status: '',
    vehicleType: 'vehicle',  // Only show cars/SUVs by default, NOT golf carts or trailers
  });
  const [sortBy, setSortBy] = useState('price-low');
  const [viewMode, setViewMode] = useState('grid');
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [selectedVehicle, setSelectedVehicle] = useState(null);
  const [selectedPaymentType, setSelectedPaymentType] = useState('deposit');

  useEffect(() => {
    window.scrollTo(0, 0);
    loadVehicles();
  }, []);

  const loadVehicles = async () => {
    console.log('Loading available vehicles from database...');
    setIsLoading(true);
    setError(null);
    
    try {
      // Use getAvailableVehicles to exclude sold vehicles from public display
      const { data, error } = await vehicleService.getAvailableVehicles();
      
      if (error) {
        console.error('Error loading vehicles:', error);
        setError('Failed to load vehicles');
        return;
      }
      
      console.log('Loaded available vehicles:', data);
      // Log the types of vehicles loaded
      const vehicleTypes = data?.map(v => v.vehicleType || v.vehicle_type || 'vehicle');
      console.log('Vehicle types in inventory:', [...new Set(vehicleTypes)]);
      console.log('Total vehicles:', data?.length, 'Golf carts:', vehicleTypes?.filter(t => t === 'golf-cart').length, 'Trailers:', vehicleTypes?.filter(t => t === 'trailer').length);
      
      setVehicles(data || []);
      // Don't set filtered vehicles here - let the filter effect handle it
      
    } catch (err) {
      console.error('Exception loading vehicles:', err);
      setError('Failed to load vehicles');
    } finally {
      setIsLoading(false);
    }
  };

  const openPayment = (vehicle) => {
    setSelectedVehicle(vehicle);
    // Vehicles take a $1,000 deposit; golf carts are full purchase
    const vType = vehicle.vehicleType || vehicle.vehicle_type || 'vehicle';
    setSelectedPaymentType(vType === 'golf-cart' ? 'full' : 'deposit');
    setShowPaymentModal(true);
  };

  const closePayment = () => {
    setShowPaymentModal(false);
    setSelectedVehicle(null);
  };

  useEffect(() => {
    console.log('Applying filters to vehicles:', vehicles.length);
    console.log('Current vehicleType filter:', filters.vehicleType);
    let filtered = vehicles.filter(vehicle => {
      const matchesMake = !filters.make || vehicle.make === filters.make;
      const matchesModel = !filters.model || vehicle.model === filters.model;
      const matchesYear = !filters.year || vehicle.year.toString() === filters.year;
      const matchesPriceMin = !filters.priceMin || vehicle.price >= parseInt(filters.priceMin);
      const matchesPriceMax = !filters.priceMax || vehicle.price <= parseInt(filters.priceMax);
      const matchesMileage = !filters.mileageMax || vehicle.mileage <= parseInt(filters.mileageMax);
      const matchesFuel = !filters.fuelType || vehicle.fuelType === filters.fuelType;
      const matchesTransmission = !filters.transmission || vehicle.transmission === filters.transmission;
      const matchesCondition = !filters.condition || vehicle.condition === filters.condition;
      const matchesStatus = !filters.status || vehicle.status === filters.status;
      // Filter by vehicle type - check both possible field names
      const vType = (vehicle.vehicleType || vehicle.vehicle_type || '').toLowerCase();
      const make = (vehicle.make || '').toLowerCase();
      const model = (vehicle.model || '').toLowerCase();
      
      // Detect golf carts by make (common golf cart brands)
      const golfCartMakes = ['ezgo', 'e-z-go', 'club car', 'clubcar', 'yamaha', 'icon', 'advanced ev', 'tomberlin', 'star ev', 'bintelli'];
      const isGolfCartByMake = golfCartMakes.some(gcMake => make.includes(gcMake)) || 
                               model.includes('golf') || model.includes('cart') ||
                               model.includes('txt') || model.includes('rxv') || model.includes('drive2');
      
      // Detect trailers by make or model
      const trailerIndicators = ['trailer', 'p&c', 'big tex', 'carry-on', 'load trail', 'pj trailer', 'diamond c'];
      const isTrailerByMake = trailerIndicators.some(t => make.includes(t) || model.includes(t));
      
      // Determine effective vehicle type
      let effectiveType = vType;
      if (!effectiveType || effectiveType === 'vehicle') {
        if (isGolfCartByMake) effectiveType = 'golf-cart';
        else if (isTrailerByMake) effectiveType = 'trailer';
      }
      
      // When filtering for 'vehicle', explicitly exclude golf-carts, trailers, and boats
      let matchesVehicleType = true;
      if (filters.vehicleType === 'vehicle') {
        // Only show automotive vehicles (exclude golf-cart, trailer, boat)
        matchesVehicleType = !['golf-cart', 'trailer', 'boat'].includes(effectiveType) && !isGolfCartByMake && !isTrailerByMake;
      } else if (filters.vehicleType) {
        // For specific type filters (golf-cart, trailer, boat), match exactly
        matchesVehicleType = effectiveType === filters.vehicleType;
      }
      
      return matchesMake && matchesModel && matchesYear && matchesPriceMin && 
             matchesPriceMax && matchesMileage && matchesFuel && matchesTransmission && 
             matchesCondition && matchesStatus && matchesVehicleType;
    });

    console.log('Filtered vehicles count:', filtered.length);
    const filteredTypes = filtered.map(v => v.vehicleType || v.vehicle_type || 'vehicle');
    console.log('Filtered vehicle types:', [...new Set(filteredTypes)]);

    // Sort vehicles
    filtered.sort((a, b) => {
      switch (sortBy) {
        case 'price-low': return a.price - b.price;
        case 'price-high': return b.price - a.price;
        case 'year-new': return b.year - a.year;
        case 'year-old': return a.year - b.year;
        case 'mileage-low': return a.mileage - b.mileage;
        case 'mileage-high': return b.mileage - a.mileage;
        default: return 0;
      }
    });

    console.log('Filtered vehicles:', filtered.length);
    setFilteredVehicles(filtered);
  }, [vehicles, filters, sortBy]);

  const handleFilterChange = (key, value) => {
    console.log('Filter changed:', key, value);
    setFilters(prev => ({
      ...prev,
      [key]: value,
    }));
  };

  const clearFilters = () => {
    setFilters({
      make: '',
      model: '',
      year: '',
      priceMin: '',
      priceMax: '',
      mileageMax: '',
      fuelType: '',
      transmission: '',
      condition: '',
      status: '',
      vehicleType: '',  // Empty string to show all vehicles
    });
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
    }).format(price);
  };

  const formatMileage = (mileage) => {
    return new Intl.NumberFormat('en-US').format(mileage);
  };

  // Create unique lists for filters
  const uniqueMakes = vehicles.length > 0 ? [...new Set(vehicles.map(v => v.make))].sort() : [];
  const uniqueYears = vehicles.length > 0 ? [...new Set(vehicles.map(v => v.year))].sort((a, b) => b - a) : [];

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 py-8">
        <div className="container mx-auto px-4">
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto mb-4"></div>
            <p className="text-gray-600">Loading vehicles...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 py-8 pt-24">
        <div className="container mx-auto px-4">
          <div className="text-center py-16 bg-white rounded-2xl shadow-lg max-w-2xl mx-auto">
            <div className="text-6xl mb-6">🔧</div>
            <h1 className="text-3xl font-bold text-gray-900 mb-4">Inventory Temporarily Unavailable</h1>
            <p className="text-gray-600 mb-2">We're experiencing a brief technical issue with our inventory system.</p>
            <p className="text-gray-600 mb-8">Our vehicles are still available — give us a call and we'll help you find exactly what you're looking for!</p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a 
                href="tel:+18173822316"
                className="bg-primary-600 hover:bg-primary-700 text-white px-8 py-4 rounded-lg font-semibold transition-colors text-lg flex items-center justify-center gap-2"
              >
                <FiPhone className="w-5 h-5" />
                Call Us: (817) 382-2316
              </a>
              <button 
                onClick={loadVehicles}
                className="bg-gray-200 hover:bg-gray-300 text-gray-700 px-8 py-4 rounded-lg font-semibold transition-colors text-lg"
              >
                Try Again
              </button>
            </div>
            <p className="text-sm text-gray-400 mt-8">Located at 110 Paradise Ln, Paradise, TX 76073</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="min-h-screen bg-gray-50 py-8 pb-20 pt-24 lg:pt-36 md:pt-16 sm:pt-20 overflow-x-hidden"
    >
      <Helmet>
        <title>Complete Inventory | All Vehicles, Golf Carts & Trailers | Paradise-Performance</title>
        <meta name="description" content="Browse our complete inventory of used cars, SUVs, golf carts, and trailers in Paradise, TX. Filter by type, price, year. Over 50+ units in stock. Financing available." />
        <link rel="canonical" href="https://paradise-performance.com/inventory" />
        <meta property="og:title" content="Complete Vehicle Inventory - Paradise-Performance" />
        <meta property="og:description" content="Browse all vehicles, golf carts, and trailers in stock. Financing available." />
        <meta property="og:url" content="https://paradise-performance.com/inventory" />
      </Helmet>

      <div className="container mx-auto px-4 overflow-x-hidden max-w-full">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-2">Vehicle Inventory</h1>
          <p className="text-gray-600 mb-2">
            Browse our selection of {filteredVehicles.length} quality used vehicles in Paradise, TX serving Dallas, Fort Worth, and Denton
          </p>
          <p className="text-sm text-primary-600 font-medium">
            <span className="inline-flex items-center gap-1">
              <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
              We offer in-house financing on select vehicles
            </span>
          </p>
          <p className="text-sm text-gray-500 mt-2">
            If you've placed a deposit or purchased a vehicle, pickup is available at our lot — 305 Co Rd 3470, Paradise, TX 76073. Call <a href="tel:8173822316" className="text-primary-600 font-medium hover:underline">(817) 382-2316</a> to schedule a test drive or arrange pickup.
          </p>
        </div>

        <div className="flex flex-col lg:flex-row gap-6 overflow-x-hidden">
          {/* Filters Sidebar */}
          <div className="w-full lg:w-1/4">
            <div className="bg-white rounded-lg shadow-lg p-4 lg:p-6 lg:sticky lg:top-24">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-semibold text-gray-900">Filters</h2>
                <button 
                  onClick={clearFilters}
                  className="text-sm text-primary-600 hover:text-primary-700"
                >
                  Clear All
                </button>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Make</label>
                  <select 
                    value={filters.make} 
                    onChange={(e) => handleFilterChange('make', e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  >
                    <option value="">All Makes</option>
                    {uniqueMakes.map(make => (
                      <option key={make} value={make}>{make}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Status</label>
                  <select 
                    value={filters.status} 
                    onChange={(e) => handleFilterChange('status', e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  >
                    <option value="">All Status</option>
                    <option value="in-transit">In Transit</option>
                    <option value="recon-pending">Recon Pending</option>
                    <option value="on-lot">Available Now</option>
                    <option value="pending-contract">Sale Pending</option>
                    <option value="sold">Sold</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Year</label>
                  <select 
                    value={filters.year} 
                    onChange={(e) => handleFilterChange('year', e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  >
                    <option value="">All Years</option>
                    {uniqueYears.map(year => (
                      <option key={year} value={year}>{year}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Price Range</label>
                  <div className="flex space-x-2">
                    <input 
                      type="number" 
                      placeholder="Min" 
                      value={filters.priceMin}
                      onChange={(e) => handleFilterChange('priceMin', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    />
                    <input 
                      type="number" 
                      placeholder="Max" 
                      value={filters.priceMax}
                      onChange={(e) => handleFilterChange('priceMax', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Fuel Type</label>
                  <select 
                    value={filters.fuelType} 
                    onChange={(e) => handleFilterChange('fuelType', e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  >
                    <option value="">All Fuel Types</option>
                    <option value="Gasoline">Gasoline</option>
                    <option value="Electric">Electric</option>
                    <option value="Hybrid">Hybrid</option>
                  </select>
                </div>
              </div>
            </div>
          </div>

          {/* Main Content */}
          <div className="w-full lg:w-3/4 overflow-x-hidden">
            {/* Toolbar */}
            <div className="bg-white rounded-lg shadow-lg p-4 mb-6">
              <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                <div className="text-sm text-gray-600">
                  Showing {filteredVehicles.length} of {vehicles.length} vehicles
                </div>
                <div className="flex items-center space-x-4">
                  <div className="flex items-center space-x-2">
                    <label className="text-sm font-medium text-gray-700">Sort by:</label>
                    <select 
                      value={sortBy} 
                      onChange={(e) => setSortBy(e.target.value)}
                      className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    >
                      <option value="price-low">Price: Low to High</option>
                      <option value="price-high">Price: High to Low</option>
                      <option value="year-new">Year: Newest First</option>
                      <option value="year-old">Year: Oldest First</option>
                      <option value="mileage-low">Mileage: Low to High</option>
                      <option value="mileage-high">Mileage: High to Low</option>
                    </select>
                  </div>
                  <div className="flex items-center space-x-2">
                    <button 
                      onClick={() => setViewMode('grid')}
                      className={`p-2 rounded transition-colors ${viewMode === 'grid' ? 'bg-primary-600 text-white' : 'bg-gray-200 text-gray-600 hover:bg-gray-300'}`}
                    >
                      <SafeIcon icon={FiGrid} className="w-4 h-4" />
                    </button>
                    <button 
                      onClick={() => setViewMode('list')}
                      className={`p-2 rounded transition-colors ${viewMode === 'list' ? 'bg-primary-600 text-white' : 'bg-gray-200 text-gray-600 hover:bg-gray-300'}`}
                    >
                      <SafeIcon icon={FiList} className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>
            </div>

            {/* Vehicle Grid with Metallic Style */}
            <style>{`
              .metallic-card {
                position: relative;
                background: linear-gradient(135deg, #e0e0e0, #c0c0c0);
                border-radius: 16px;
                box-shadow:
                  4px 4px 8px rgba(0, 0, 0, 0.2),
                  -4px -4px 8px rgba(255, 255, 255, 0.2),
                  inset 1px 1px 2px rgba(255, 255, 255, 0.3),
                  inset -1px -1px 2px rgba(0, 0, 0, 0.3);
                transition:
                  transform 0.3s ease,
                  box-shadow 0.3s ease;
                overflow: hidden;
                display: flex;
                flex-direction: column;
                max-width: 100%;
              }
              
              @media (min-width: 768px) {
                .metallic-card {
                  min-height: 410px;
                  border-radius: 30px;
                  box-shadow:
                    6px 6px 12px rgba(0, 0, 0, 0.3),
                    -6px -6px 12px rgba(255, 255, 255, 0.3),
                    inset 2px 2px 4px rgba(255, 255, 255, 0.4),
                    inset -2px -2px 4px rgba(0, 0, 0, 0.5);
                }
              }

              .metallic-card.golf-cart-card {
                background: linear-gradient(135deg, #3a3a3a, #1a1a1a);
                box-shadow:
                  6px 6px 12px rgba(0, 0, 0, 0.5),
                  -6px -6px 12px rgba(100, 100, 100, 0.1),
                  inset 2px 2px 4px rgba(80, 80, 80, 0.3),
                  inset -2px -2px 4px rgba(0, 0, 0, 0.8);
              }

              .metallic-card::before {
                content: "";
                position: absolute;
                top: 0;
                left: 0;
                width: 100%;
                height: 100%;
                background: linear-gradient(
                  45deg,
                  transparent,
                  rgba(255, 255, 255, 0.7),
                  transparent
                );
                background-size: 200% 200%;
                animation: shine 3s infinite ease-in-out;
                border-radius: inherit;
                pointer-events: none;
                z-index: 1;
              }

              .golf-cart-card::before {
                background: linear-gradient(
                  45deg,
                  transparent,
                  rgba(150, 150, 150, 0.5),
                  transparent
                );
              }

              .metallic-card:hover {
                transform: translateY(-5px);
                box-shadow:
                  8px 8px 16px rgba(0, 0, 0, 0.35),
                  -8px -8px 16px rgba(255, 255, 255, 0.35),
                  inset 2px 2px 4px rgba(255, 255, 255, 0.5),
                  inset -2px -2px 4px rgba(0, 0, 0, 0.6);
              }

              .golf-cart-card:hover {
                box-shadow:
                  8px 8px 16px rgba(0, 0, 0, 0.6),
                  -8px -8px 16px rgba(100, 100, 100, 0.15),
                  inset 2px 2px 4px rgba(80, 80, 80, 0.4),
                  inset -2px -2px 4px rgba(0, 0, 0, 0.9);
              }

              .metallic-card-content {
                position: relative;
                z-index: 2;
                flex: 1;
                display: flex;
                flex-direction: column;
              }

              @keyframes shine {
                0% {
                  background-position: 0% 50%;
                }
                50% {
                  background-position: 100% 50%;
                }
                100% {
                  background-position: 0% 50%;
                }
              }

              .metallic-image-container {
                position: relative;
                border-radius: 20px 20px 0 0;
                overflow: hidden;
                background: linear-gradient(135deg, #f0f0f0, #d0d0d0);
                box-shadow: inset 0 2px 4px rgba(0, 0, 0, 0.2);
              }

              .metallic-button {
                background: linear-gradient(135deg, #4a90e2, #357abd);
                box-shadow:
                  2px 2px 4px rgba(0, 0, 0, 0.3),
                  -2px -2px 4px rgba(255, 255, 255, 0.3),
                  inset 1px 1px 2px rgba(255, 255, 255, 0.3);
                border: none;
                margin-top: 14%;
                margin-bottom: 0;
                border-radius: 12px;
                transition: all 0.3s ease;
              }

              .metallic-button:hover {
                background: linear-gradient(135deg, #5ba0f2, #4680cd);
                box-shadow:
                  3px 3px 6px rgba(0, 0, 0, 0.35),
                  -3px -3px 6px rgba(255, 255, 255, 0.35),
                  inset 1px 1px 2px rgba(255, 255, 255, 0.4);
                transform: translateY(-1px);
              }

              .metallic-heart {
                background: linear-gradient(135deg, #ffffff, #e0e0e0);
                box-shadow:
                  2px 2px 4px rgba(0, 0, 0, 0.2),
                  -2px -2px 4px rgba(255, 255, 255, 0.5);
                border-radius: 50%;
              }

              .metallic-heart:hover {
                background: linear-gradient(135deg, #ff6b6b, #ff4444);
                box-shadow:
                  3px 3px 6px rgba(0, 0, 0, 0.25),
                  -3px -3px 6px rgba(255, 255, 255, 0.5);
              }
              
              /* Golf Cart Card Specific Text Styles */
              .golf-cart-card .text-gray-800 {
                color: #e0e0e0 !important;
              }
              
              .golf-cart-card .text-gray-700 {
                color: #d0d0d0 !important;
              }
              
              .golf-cart-card .text-gray-600 {
                color: #b0b0b0 !important;
              }
              
              .golf-cart-card .text-gray-500 {
                color: #a0a0a0 !important;
              }
              
              .golf-cart-card .bg-gradient-to-br.from-gray-200.to-gray-300 {
                background: linear-gradient(135deg, #505050, #404040) !important;
                color: #e0e0e0 !important;
              }
              
              .golf-cart-card .metallic-button {
                background: linear-gradient(135deg, #2a7a2a, #1a5a1a) !important;
                box-shadow:
                  2px 2px 4px rgba(0, 0, 0, 0.4),
                  -2px -2px 4px rgba(100, 100, 100, 0.2),
                  inset 1px 1px 2px rgba(150, 255, 150, 0.2) !important;
              }
              
              .golf-cart-card .metallic-button:hover {
                background: linear-gradient(135deg, #3a8a3a, #2a6a2a) !important;
                box-shadow:
                  3px 3px 6px rgba(0, 0, 0, 0.45),
                  -3px -3px 6px rgba(100, 100, 100, 0.25),
                  inset 1px 1px 2px rgba(150, 255, 150, 0.3) !important;
              }
              
              .golf-cart-card .metallic-heart {
                background: linear-gradient(135deg, #505050, #303030) !important;
                box-shadow:
                  2px 2px 4px rgba(0, 0, 0, 0.3),
                  -2px -2px 4px rgba(100, 100, 100, 0.2) !important;
              }
              
              .golf-cart-card .metallic-image-container {
                background: linear-gradient(135deg, #404040, #202020) !important;
                box-shadow: inset 0 2px 4px rgba(0, 0, 0, 0.4) !important;
              }
              
              .golf-cart-card .bg-gradient-to-br.from-blue-600.to-blue-800 {
                background: linear-gradient(135deg, #4ade80, #22c55e) !important;
                -webkit-background-clip: text !important;
                -webkit-text-fill-color: transparent !important;
                background-clip: text !important;
              }
              
              .golf-cart-card .bg-gradient-to-br.from-blue-200.to-blue-300 {
                background: linear-gradient(135deg, #606060, #505050) !important;
                color: #10b981 !important;
              }
            `}</style>
            {filteredVehicles.length > 0 ? (
              <div className={`grid gap-4 mb-12 pb-8 ${viewMode === 'grid' ? 'grid-cols-1 md:grid-cols-2 xl:grid-cols-3' : 'grid-cols-1'}`}>
                {filteredVehicles.map((vehicle) => (
                  <motion.div
                    key={vehicle.id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    className={`metallic-card ${
                      (vehicle.vehicleType === 'golf-cart' || 
                       vehicle.vehicle_type === 'golf-cart' ||
                       (vehicle.vehicleType || vehicle.vehicle_type || '').toLowerCase().includes('golf')) 
                      ? 'golf-cart-card' : ''
                    }`}
                  >
                    <div className="metallic-card-content flex flex-col h-full">
                      <div className="metallic-image-container">
                        <img 
                          src={vehicle.images?.[0] || 'https://images.unsplash.com/photo-1494976688153-d4d4c0c8e0b0?w=400&h=300&fit=crop'} 
                          alt={`${vehicle.year} ${vehicle.make} ${vehicle.model}`}
                          className="w-full h-48 object-cover"
                          onError={(e) => {
                            e.target.src = 'https://images.unsplash.com/photo-1494976688153-d4d4c0c8e0b0?w=400&h=300&fit=crop';
                          }}
                        />
                        <div className="absolute top-3 left-3 flex flex-col gap-2 z-10">
                          <span className={`px-3 py-1 rounded-full text-xs font-bold shadow-lg ${
                            vehicle.condition === 'new' ? 'bg-gradient-to-r from-green-400 to-green-600 text-white' : 
                            vehicle.condition === 'professional' ? 'bg-gradient-to-r from-purple-400 to-purple-600 text-white' : 
                            'bg-gradient-to-r from-blue-400 to-blue-600 text-white'
                          }`}>
                            {vehicle.condition === 'professional' ? 'professional' : 
                             vehicle.condition === 'new' ? 'New' : 'Used'}
                          </span>
                          <span className={`px-3 py-1 rounded-full text-xs font-bold shadow-lg ${
                            vehicle.status === 'in-transit' ? 'bg-gradient-to-r from-yellow-400 to-yellow-600 text-white' :
                            vehicle.status === 'recon-pending' ? 'bg-gradient-to-r from-orange-400 to-orange-600 text-white' :
                            vehicle.status === 'on-lot' ? 'bg-gradient-to-r from-green-400 to-green-600 text-white' :
                            vehicle.status === 'pending-contract' ? 'bg-gradient-to-r from-gray-700 to-black text-white' :
                            'bg-gradient-to-r from-cyan-400 to-cyan-600 text-white'
                          }`}>
                            {vehicle.status === 'in-transit' ? 'In Transit' :
                             vehicle.status === 'recon-pending' ? 'Recon Pending' :
                             vehicle.status === 'on-lot' ? 'Available' :
                             vehicle.status === 'pending-contract' ? 'Sale Pending' :
                             'Sold'}
                          </span>
                          {((vehicle.vehicleType || vehicle.vehicle_type || '').toLowerCase().includes('golf')) && (
                            <span className="px-3 py-1 rounded-full text-xs font-bold bg-gradient-to-r from-emerald-400 to-emerald-600 text-white shadow-lg">
                              Golf Cart
                            </span>
                          )}
                        </div>
                        <div className="absolute top-3 right-3 z-10">
                          <button className="metallic-heart p-2 transition-all">
                            <SafeIcon icon={FiHeart} className="w-4 h-4 text-gray-600" />
                          </button>
                        </div>
                      </div>
                      
                      <div className="p-4 md:p-6 flex flex-col">
                        {/* Content Section */}
                        <div>
                          <div className="flex justify-between space-around items-start mb-2 md:mb-3">
                            <div>
                              <h3 className="text-lg font-bold text-gray-800 mb-1">
                                {formatVehicleTitle(vehicle)}
                              </h3>
                              <p className="text-sm text-gray-600 font-medium">Stock #{vehicle.stockNumber}</p>
                            </div>
                            <div className="text-right">
                              <div className="text-2xl font-bold bg-gradient-to-br from-blue-600 to-blue-800 bg-clip-text text-transparent">
                                {formatPrice(vehicle.price)}
                              </div>
                            </div>
                          </div>
                        
                          <div className="flex items-center text-sm text-gray-600 mb-2 space-x-4">
                            <span>{formatMileage(vehicle.mileage)} mi</span>
                            <span>•</span>
                            <span>{vehicle.fuelType}</span>
                            <span>•</span>
                            <span>{vehicle.combinedMPG} MPG</span>
                          </div>
                        
                          {vehicle.vin && (
                            <div className="text-sm text-gray-500 font-mono truncate mb-2" title={vehicle.vin}>
                              VIN: {vehicle.vin}
                            </div>
                          )}
                        
                          {vehicle.features && vehicle.features.length > 0 && (
                            <div className="flex flex-wrap gap-2 mb-3 md:mb-4">
                              {vehicle.features?.slice(0, 3).map((feature, index) => (
                                <span 
                                  key={index}
                                  className="px-2 py-1 bg-gradient-to-br from-gray-200 to-gray-300 text-gray-700 text-xs rounded-md font-medium shadow-sm"
                                >
                                  {feature}
                                </span>
                              ))}
                              {vehicle.features?.length > 3 && (
                                <span className="px-2 py-1 bg-gradient-to-br from-blue-200 to-blue-300 text-blue-800 text-xs rounded-md font-bold shadow-sm">
                                  +{vehicle.features.length - 3} more
                                </span>
                              )}
                            </div>
                          )}
                        </div>
                        
                        {/* Buttons Section */}
                        <div className="flex space-x-2 mt-1">
                        <Link 
                          to={(() => {
                            const vehicleType = (vehicle.vehicleType || vehicle.vehicle_type || '').toLowerCase();
                            
                            if (vehicleType.includes('golf') || vehicleType.includes('cart')) {
                              return `/golf-cart/${vehicle.id}`;
                            } else if (vehicleType.includes('trailer')) {
                              return `/trailer/${vehicle.id}`;
                            } else {
                              // Default route for regular vehicles
                              return `/vehicle/${vehicle.id}`;
                            }
                          })()}
                          className="flex-1 metallic-button text-white px-4 py-3 pb-1 font-bold text-center"
                        >
                          View Details
                        </Link>
                        <a
                          href="tel:+19025554567"
                          className="metallic-button text-white px-4 py-3 font-bold"
                          title="Call about this vehicle"
                        >
                          <SafeIcon icon={FiPhone} className="w-5 h-5" />
                        </a>
                      </div>
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>
            ) : (
              <div className="text-center py-12 metallic-card p-8">
                <div className="metallic-card-content">
                  <div className="text-gray-700 text-lg mb-4 font-semibold">
                    {vehicles.length === 0 ? 'No vehicles available' : 'No vehicles match your search criteria'}
                  </div>
                  <p className="text-gray-600 mb-6">
                    Try adjusting your filters or contact us for assistance finding your perfect vehicle.
                  </p>
                  <button
                    onClick={clearFilters}
                    className="metallic-button text-white px-6 py-3 font-bold"
                  >
                    Clear All Filters
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Payment Modal */}
      <PaymentModal
        isOpen={showPaymentModal}
        onClose={closePayment}
        vehicle={selectedVehicle}
        paymentType={selectedPaymentType}
        maxAmount={100000}
      />
      {/* Policy Notice */}
      <div className="container mx-auto px-4 py-4 pb-16 text-center">
        <p className="text-xs text-gray-500">
          All deposits are non-refundable. Please review our{' '}
          <Link to="/refund-policy" className="text-primary-600 hover:text-primary-700 underline">
            Refund & Return Policy
          </Link>{' '}
          before making a purchase.
        </p>
      </div>
    </motion.div>
  );
};

export default Inventory;